import React from 'react';
import {useTranslation} from 'react-i18next';

import type {LogoSizeModifier} from '@pexip/components';
import {IconTypes, Icon} from '@pexip/components';
import type {InputDeviceStatusInfo} from '../types';

export const useInputsState = ({
    isAudioInputMuted,
    isVideoInputMuted,
    inputStatusInfo,
    iconSize,
}: {
    isVideoInputMuted: boolean;
    isAudioInputMuted: boolean;
    inputStatusInfo: InputDeviceStatusInfo;
    iconSize: LogoSizeModifier;
}) => {
    const {t} = useTranslation();
    const hasAudioError = Boolean(inputStatusInfo.audio.error);
    const hasVideoError = Boolean(inputStatusInfo.video.error);

    const isAudioIconMuted = hasAudioError || isAudioInputMuted;
    const isVideoIconMuted = hasVideoError || isVideoInputMuted;

    const audioInputAction = isAudioIconMuted
        ? t('media.unmute-microphone', 'Unmute microphone')
        : t('media.mute-microphone', 'Mute microphone');
    const videoInputAction = isVideoIconMuted
        ? t('media.turn-camera-on', 'Turn camera on')
        : t('media.turn-camera-off', 'Turn camera off');

    return {
        audioProps: {
            hasError: hasAudioError,
            hasAudioVideoError: false,
            isActive: isAudioInputMuted,
            'aria-label': audioInputAction,
            icon: isAudioIconMuted ? (
                <Icon source={IconTypes.IconMicrophoneOff} size={iconSize} />
            ) : (
                <Icon source={IconTypes.IconMicrophoneOn} size={iconSize} />
            ),
        },
        videoProps: {
            hasError: hasVideoError,
            hasAudioVideoError: false,
            isActive: isVideoInputMuted,
            'aria-label': videoInputAction,
            icon: isVideoIconMuted ? (
                <Icon source={IconTypes.IconVideoOff} size={iconSize} />
            ) : (
                <Icon source={IconTypes.IconVideoOn} size={iconSize} />
            ),
        },
        audioWarning: inputStatusInfo.audio.warning,
        videoWarning: inputStatusInfo.video.warning,
        isAudioIconMuted,
        isVideoIconMuted,
    };
};
